import { MutableRefObject, useMemo, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { HStack, Box } from '@chakra-ui/react';
import { MetricTypeIcon, MetricTypes } from 'Molecules/MetricTypeIcon';
import { TruncatableText, Tag, IconButton } from 'Atoms';
import { HelpIcon } from 'Tokens/Icons/Status';
import { EsrsMetricAdditionalType_, QuestionType_Enum_ } from 'models';
import { Typography } from 'Tokens';
import {
  MaterialityStatus,
  MetricMaterialityState,
} from '../../DoubleMaterialityAssessment/DoubleMaterialityAssessment.d';
import { MaterialityMenuCell } from './MaterialityMenuCell';
import { BaseTableMetric } from './types';
import { FrameworkRefs, Frameworks } from 'containers/Esrs/pieces/Assessment';
import { MetricNameWithTag } from 'containers/Esrs/pieces/DataCollection/DataCollectionUtils';

interface UseTableColumnsProps<T extends BaseTableMetric<any>> {
  framework: string;
  selectOptions: Array<{ label: string; value: MetricMaterialityState }>;
  onUpdateMateriality: (data: {
    selection: MetricMaterialityState;
    metric: T['metric'];
    materialStandardId?: string;
    isChild?: boolean;
  }) => void;
  onDeleteMetric: (metric: T['metric']) => void;
  onOpenLearnMore: (metric: T['metric']) => void;
  getMateriality: (metric: T['metric']) => { materiality: MetricMaterialityState };
  getReference: (metric: T['metric']) => string;
  isNotAssessedOrNotMaterial: boolean;
  isDrView: boolean;
  getParentMetricMaterialityAndDataCollection: (metric: T['metric']) => {
    isParentMetricMaterial: boolean | null | undefined;
    isParentMetricGroupLevel: boolean;
  };
}

export const useTableColumns = <T extends BaseTableMetric<any>>({
  framework,
  selectOptions,
  onUpdateMateriality,
  onDeleteMetric,
  onOpenLearnMore,
  getMateriality,
  getReference,
  isNotAssessedOrNotMaterial,
  isDrView,
  getParentMetricMaterialityAndDataCollection,
}: UseTableColumnsProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  return useMemo(
    () =>
      [
        {
          header: 'Data point',
          meta: { width: isDrView ? '50%' : '62%' },
          cell: ({ row }: { row: { original: T } }) => (
            <HStack>
              <MetricTypeIcon
                type={
                  row.original.type === QuestionType_Enum_.Decimal_
                    ? MetricTypes.number
                    : MetricTypes.text
                }
                color={
                  row.original.framework === framework || isNotAssessedOrNotMaterial
                    ? 'text.disabled'
                    : undefined
                }
              />
              <Box ref={ref} width="100%">
                <MetricNameWithTag
                  name={row.original.title}
                  tags={row.original.metric.tags.map((tag: { type: string }) => tag.type)}
                  showTagsToolTip={false}
                  rowRef={ref as MutableRefObject<HTMLDivElement>}
                />
              </Box>
            </HStack>
          ),
        },
        {
          header: 'Framework',
          meta: { width: '136px' },
          cell: ({ row }: { row: { original: T } }) => (
            <Typography
              variant="body"
              color={
                row.original.framework === framework || isNotAssessedOrNotMaterial
                  ? 'text.disabled'
                  : undefined
              }
            >
              {Frameworks[row.original.framework as keyof typeof FrameworkRefs]}
            </Typography>
          ),
        },
        {
          header: 'Ref.',
          meta: { width: '128px' },
          cell: ({ row }: { row: { original: T } }) => (
            <Box w="128px">
              <TruncatableText
                variant="body"
                maxW="128px"
                text={getReference(row.original.metric)}
                color={
                  row.original.framework === framework || isNotAssessedOrNotMaterial
                    ? 'text.disabled'
                    : undefined
                }
              />
            </Box>
          ),
        },
        {
          header: 'Sub-category',
          meta: { width: '136px' },
          cell: ({ row }: { row: { original: T } }) => (
            <Box w="136px">
              <Tag
                title={row.original.subCategory}
                truncatable
                size="xs"
                disabled={row.original.framework === framework || isNotAssessedOrNotMaterial}
              />
            </Box>
          ),
        },
        {
          header: 'In your assessment',
          meta: { width: '112px' },
          cell: ({ row }: { row: { original: T } }) => {
            const met = row.original.metric;
            const hasPhasedInOption = met.additionalTypes.find(
              (additionalType: EsrsMetricAdditionalType_) =>
                additionalType?.additionalType?.title?.includes('Phased')
            );
            const { materiality } = getMateriality(row.original.metric);
            const isLocked = materiality === MetricMaterialityState.collectDataMandatory;
            const { isParentMetricMaterial, isParentMetricGroupLevel } =
              getParentMetricMaterialityAndDataCollection(row.original.metric);

            const selectOptionsFiltered = selectOptions.filter((option) => {
              if (isParentMetricMaterial && !isParentMetricGroupLevel) {
                if (!hasPhasedInOption)
                  return (
                    option.value !== MetricMaterialityState.notMaterial &&
                    option.value !== MetricMaterialityState.phasedIn
                  );
                return option.value !== MetricMaterialityState.notMaterial;
              }
              if (!hasPhasedInOption) return option.value !== MetricMaterialityState.phasedIn;
              return true;
            });

            return (
              <MaterialityMenuCell
                metric={row.original.metric}
                materiality={materiality}
                isLocked={isLocked}
                metricFramework={row.original.metric.requirement.group.standard.frameworkRef}
                selectOptions={selectOptionsFiltered}
                onUpdateMateriality={onUpdateMateriality}
                onDeleteMetric={onDeleteMetric}
                materialStandardId={row.original.materialStandardId}
                isDropDownDisabled={isNotAssessedOrNotMaterial}
                disableNotIncluded={(isParentMetricMaterial && !isParentMetricGroupLevel) ?? false}
              />
            );
          },
        },
        {
          id: 'help',
          header: '',
          meta: { width: '136px' },
          cell: ({ row }: { row: { original: T } }) => (
            <Box w="48px">
              <IconButton
                aria-label="learn more"
                variant="ghost"
                icon={<HelpIcon />}
                size="md"
                onClick={() => onOpenLearnMore(row.original.metric)}
              />
            </Box>
          ),
        },
      ].filter((col) => (col.header === 'Sub-category' ? isDrView : true)) as ColumnDef<T>[],
    [
      framework,
      selectOptions,
      onUpdateMateriality,
      onDeleteMetric,
      onOpenLearnMore,
      getMateriality,
      getReference,
    ]
  );
};
